import React, { useCallback, useEffect, useState } from 'react';
import { X, Mail } from 'lucide-react';
export function openContactModal() {
  window.dispatchEvent(new CustomEvent('open-contact-modal'));
}
export function ContactModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    subject: 'General Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleOpen = useCallback(() => {
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    document.body.style.overflow = '';
    // Reset form after animation finishes
    setTimeout(() => {
      setIsSuccess(false);
      setFormData({
        name: '',
        email: '',
        organisation: '',
        subject: 'General Inquiry',
        message: ''
      });
    }, 300);
  }, []);
  useEffect(() => {
    window.addEventListener('open-contact-modal', handleOpen);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('open-contact-modal', handleOpen);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleOpen, handleClose, isOpen]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      // Auto close after success
      setTimeout(() => {
        handleClose();
      }, 2500);
    }, 1500);
  };
  if (!isOpen && !isSubmitting && !isSuccess) return null;
  return <div className={`fixed inset-0 z-[9998] flex items-center justify-center p-4 sm:p-6 transition-all duration-500 ease-out ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-16">
      {/* Backdrop */}
      <div className={`absolute inset-0 bg-brand-charcoal/60 transition-all duration-500 ease-out ${isOpen ? 'backdrop-blur-md' : 'backdrop-blur-none'}`} onClick={handleClose} data-id="element-17" />

      {/* Modal Card */}
      <div className={`relative w-full max-w-2xl bg-brand-cream shadow-2xl overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${isOpen ? 'scale-100 translate-y-0 opacity-100' : 'scale-[0.97] translate-y-6 opacity-0'}`} onClick={e => e.stopPropagation()} data-id="element-18">
        {/* Accent top border */}
        <div className="h-1 w-full bg-gradient-to-r from-brand-red via-brand-gold to-brand-charcoal" data-id="element-19" />

        {/* Close Button */}
        <button onClick={handleClose} className="absolute top-5 right-5 p-2.5 text-brand-brown hover:text-brand-red transition-colors rounded-full hover:bg-brand-shape/20 z-10" aria-label="Close modal" data-id="element-20">
          <X className="w-5 h-5" data-id="element-21" />
        </button>

        <div className="p-6 sm:p-8 max-h-[90vh] overflow-y-auto" data-id="element-22">
          <div className="mb-6" data-id="element-23">
            <h3 className="text-2xl font-serif text-brand-charcoal mb-2" data-id="element-24">
              Get in touch
            </h3>
            <p className="text-brand-brown text-base" data-id="element-25">
              We'd love to hear from you. Fill in the form below and a member of
              the Firetree team will respond shortly.
            </p>
          </div>

          {isSuccess ? <div className="bg-brand-card/40 border border-brand-gold/30 p-8 text-center py-10" data-id="element-26">
              <div className="w-16 h-16 bg-brand-red/10 rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-27">
                <Mail className="w-8 h-8 text-brand-red" data-id="element-28" />
              </div>
              <h4 className="text-xl font-serif text-brand-charcoal mb-3" data-id="element-29">
                Message sent successfully
              </h4>
              <p className="text-brand-brown leading-relaxed" data-id="element-30">
                Thank you for reaching out. A member of the Firetree team will
                get back to you shortly.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-31">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-32">
                <div data-id="element-33">
                  <label htmlFor="name" className="block text-sm font-semibold tracking-wide uppercase text-brand-charcoal mb-2" data-id="element-34">
                    Full Name <span className="text-brand-red" data-id="element-35">*</span>
                  </label>
                  <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full bg-white/60 border border-brand-shape/50 px-4 py-3 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all placeholder:text-brand-brown/40" placeholder="Jane Doe" data-id="element-36" />
                </div>
                <div data-id="element-37">
                  <label htmlFor="email" className="block text-sm font-semibold tracking-wide uppercase text-brand-charcoal mb-2" data-id="element-38">
                    Email Address <span className="text-brand-red" data-id="element-39">*</span>
                  </label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full bg-white/60 border border-brand-shape/50 px-4 py-3 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all placeholder:text-brand-brown/40" placeholder="jane@example.com" data-id="element-40" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-41">
                <div data-id="element-42">
                  <label htmlFor="organisation" className="block text-sm font-semibold tracking-wide uppercase text-brand-charcoal mb-2" data-id="element-43">
                    Organisation{' '}
                    <span className="text-brand-brown/50 font-normal normal-case tracking-normal" data-id="element-44">
                      (Optional)
                    </span>
                  </label>
                  <input type="text" id="organisation" name="organisation" value={formData.organisation} onChange={handleChange} className="w-full bg-white/60 border border-brand-shape/50 px-4 py-3 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all placeholder:text-brand-brown/40" placeholder="Foundation Name" data-id="element-45" />
                </div>
                <div data-id="element-46">
                  <label htmlFor="subject" className="block text-sm font-semibold tracking-wide uppercase text-brand-charcoal mb-2" data-id="element-47">
                    Subject <span className="text-brand-red" data-id="element-48">*</span>
                  </label>
                  <select id="subject" name="subject" required value={formData.subject} onChange={handleChange} className="w-full bg-white/60 border border-brand-shape/50 px-4 py-3 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all appearance-none" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B6560'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center',
                backgroundSize: '1.2em 1.2em'
              }} data-id="element-49">
                    <option value="General Inquiry" data-id="element-50">General Inquiry</option>
                    <option value="Fund Information" data-id="element-51">Fund Information</option>
                    <option value="Other" data-id="element-52">Other</option>
                  </select>
                </div>
              </div>

              <div data-id="element-53">
                <label htmlFor="message" className="block text-sm font-semibold tracking-wide uppercase text-brand-charcoal mb-2" data-id="element-54">
                  Message <span className="text-brand-red" data-id="element-55">*</span>
                </label>
                <textarea id="message" name="message" required rows={3} value={formData.message} onChange={handleChange} className="w-full bg-white/60 border border-brand-shape/50 px-4 py-3 text-brand-charcoal focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red/20 transition-all resize-none placeholder:text-brand-brown/40" placeholder="How can we help you?" data-id="element-56"></textarea>
              </div>

              <div className="pt-1" data-id="element-57">
                <button type="submit" disabled={isSubmitting} className="w-full inline-flex items-center justify-center px-8 py-4 bg-brand-red text-white font-semibold tracking-wide hover:bg-brand-charcoal transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed rounded-none" data-id="element-58">
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>}
        </div>
      </div>
    </div>;
}